import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { BookOpen, Flame, Headphones, Moon, Plus, Sun, Target, Trash2 } from 'lucide-react';
import { SALAH, dayKeyIn } from '@shared/prayer';
import { alive, useProgress, useSettings } from '@/lib/store';
import type { Goal, GoalKind } from '@/lib/types';
import { Card, Empty, LargeTitle, Ring, Row, Section, Segmented, Sheet, Stepper, fmtDuration } from '@/components/ui';
import { adhkarDone, bestStreak, goalProgress, lastNDays, prayerScore, secondsOn, streak, weekSeries } from './stats';

export function useTodayKey() {
  return dayKeyIn(useSettings((s) => s.settings.prayer.tz));
}

const SERIES = [
  { key: 'read', label: 'Reading', color: 'var(--accent)' },
  { key: 'listen', label: 'Listening', color: '#5ac8fa' },
  { key: 'adhkar', label: 'Adhkar', color: '#bf5af2' },
] as const;

function WeekChart({ data }: { data: ReturnType<typeof weekSeries> }) {
  const max = Math.max(10, ...data.map((d) => d.read + d.listen + d.adhkar));
  const W = 320, H = 140, bw = 26, gap = (W - bw * data.length) / (data.length - 1);
  return (
    <figure>
      <svg viewBox={`0 0 ${W} ${H + 20}`} className="w-full" role="img" aria-label="Minutes per day for the last 7 days">
        {[0.5, 1].map((f) => <line key={f} x1={0} x2={W} y1={H - H * f} y2={H - H * f} stroke="var(--hairline)" />)}
        {data.map((d, i) => {
          let y = H;
          const x = i * (bw + gap);
          return (
            <g key={d.dayKey}>
              <title>{`${d.dayKey}: ${Math.round(d.read + d.listen + d.adhkar)} min`}</title>
              {SERIES.map((s) => {
                const h = (d[s.key] / max) * H;
                y -= h;
                return h > 0 ? <rect key={s.key} x={x} y={y} width={bw} height={Math.max(h, 1)} rx={4} fill={s.color} /> : null;
              })}
              <text x={x + bw / 2} y={H + 15} textAnchor="middle" fontSize="11" fill="var(--muted)">
                {new Date(`${d.dayKey}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' })}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-2 flex flex-wrap gap-3 text-[12px] text-muted">
        {SERIES.map((s) => <span key={s.key} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />{s.label}</span>)}
        <span className="ml-auto">max {Math.round(max)} min</span>
      </figcaption>
    </figure>
  );
}

export function ProgressPage() {
  const today = useTodayKey();
  const sessionsMap = useProgress((s) => s.sessions);
  const prayers = useProgress((s) => s.prayers);
  const adhkar = useProgress((s) => s.adhkar);
  const goalsMap = useProgress((s) => s.goals);
  const dailyGoalMinutes = useSettings((s) => s.settings.dailyGoalMinutes);
  const [params, setParams] = useSearchParams();
  const [wizard, setWizard] = useState(params.get('goal') === 'new');
  useEffect(() => { if (params.get('goal') === 'new') { setWizard(true); setParams({}, { replace: true }); } }, [params, setParams]);

  const sessions = useMemo(() => alive(sessionsMap), [sessionsMap]);
  const goals = alive(goalsMap).filter((g) => g.active);
  const week = useMemo(() => weekSeries(sessions, today), [sessions, today]);
  const quranDays = useMemo(() => [...new Set(sessions.filter((s) => s.kind !== 'adhkar').map((s) => s.dayKey))], [sessions]);
  const quranStreak = streak(today, (d) => quranDays.includes(d));
  const prayerStreak = streak(today, (d) => prayerScore(prayers[d]) === 5);
  const morningStreak = streak(today, (d) => adhkarDone(adhkar[d]).morning);
  const eveningStreak = streak(today, (d) => adhkarDone(adhkar[d]).evening);
  const todayMin = Math.floor(secondsOn(sessions, today) / 60);
  const total = (k: string) => sessions.filter((s) => s.kind === k).reduce((a, s) => a + s.seconds, 0);
  const last7 = lastNDays(today, 7);

  return (
    <div className="page-pad">
      <LargeTitle back="/" title="Progress" ar="مسيرتك" subtitle="Your journey" />

      <div className="px-4 pb-5">
        <Card className="flex items-center gap-5 p-5">
          <Ring value={todayMin / dailyGoalMinutes} size={112} stroke={12}>
            <div><div className="text-[26px] font-bold tabular-nums">{todayMin}</div><div className="text-[12px] text-muted">of {dailyGoalMinutes} min</div></div>
          </Ring>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Today with the Qur'an</div>
            <div className="flex items-center gap-2 text-[15px]"><BookOpen size={15} className="text-accent" /> {fmtDuration(secondsOn(sessions, today, ['read']))} reading</div>
            <div className="flex items-center gap-2 text-[15px]"><Headphones size={15} className="text-[#5ac8fa]" /> {fmtDuration(secondsOn(sessions, today, ['listen', 'recite']))} listening</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-3 px-4 pb-5">
        <StreakTile label="Qur'an" value={quranStreak.current} best={bestStreak(quranDays)} />
        <StreakTile label="5 prayers" value={prayerStreak.current} best={bestStreak(Object.keys(prayers).filter((d) => prayerScore(prayers[d]) === 5))} />
        <StreakTile label="Adhkar" value={Math.min(morningStreak.current, eveningStreak.current)} best={bestStreak(Object.keys(adhkar).filter((d) => adhkarDone(adhkar[d]).morning && adhkarDone(adhkar[d]).evening))} />
      </div>

      <Section title="This week">
        <div className="p-4"><WeekChart data={week} /></div>
      </Section>

      <Section title="Khatmah plans" footer="Recurring khatmahs with one-āyah-at-a-time reading, āyah counts and ḥasanāt.">
        <Row label="Open khatmah plans" to="/khatmah" />
      </Section>

      <Section title="Goals" footer="Goals drive your daily ring and the evening reminder.">
        {goals.length === 0 && <Empty icon={<Target size={32} />} title="No goals yet" body="Set a khatm plan, a daily minutes target or a memorisation goal." />}
        {goals.map((g) => <GoalRow key={g.id} goal={g} />)}
        <Row icon={<Plus size={18} />} label="New goal" onClick={() => setWizard(true)} />
      </Section>

      <Section title="Prayers — last 7 days" footer={<Link to="/prayer/stats" className="font-semibold text-accent">See weekly, monthly & yearly prayer stats and streaks →</Link>}>
        <div className="overflow-x-auto p-4">
          <table className="w-full text-center text-[12px]">
            <thead><tr><th />{last7.map((d) => <th key={d} className="pb-2 font-medium text-muted">{new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })}</th>)}</tr></thead>
            <tbody>
              {SALAH.map((p) => (
                <tr key={p}>
                  <td className="pr-2 text-left font-medium capitalize">{p}</td>
                  {last7.map((d) => {
                    const m = prayers[d]?.marks[p];
                    const color = m === 'jamaah' ? 'bg-accent' : m === 'ontime' ? 'bg-ok' : m === 'late' ? 'bg-gold' : m === 'qada' ? 'bg-faint' : m === 'missed' ? 'bg-danger' : 'bg-fill';
                    return <td key={d} className="py-1"><span className={`mx-auto block h-5 w-5 rounded-md ${color}`} title={m ?? 'not logged'} /></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Adhkar — last 7 days">
        {(['morning', 'evening'] as const).map((w) => (
          <div key={w} className="row">
            {w === 'morning' ? <Sun size={18} className="text-gold" /> : <Moon size={18} className="text-gold" />}
            <span className="flex-1 capitalize">{w}</span>
            <div className="flex gap-1.5">{last7.map((d) => <span key={d} className={`h-4 w-4 rounded-full ${adhkarDone(adhkar[d])[w] ? 'bg-accent' : 'bg-fill'}`} title={d} />)}</div>
          </div>
        ))}
      </Section>

      <Section title="All time">
        <Row label="Reading" detail={fmtDuration(total('read'))} />
        <Row label="Listening" detail={fmtDuration(total('listen') + total('recite'))} />
        <Row label="Adhkar" detail={fmtDuration(total('adhkar'))} />
      </Section>

      <GoalWizard open={wizard} onClose={() => setWizard(false)} />
    </div>
  );
}

function StreakTile({ label, value, best }: { label: string; value: number; best: number }) {
  return (
    <Card className="p-3 text-center">
      <Flame size={20} className={`mx-auto ${value ? 'text-[#ff9f0a]' : 'text-faint'}`} />
      <div className="mt-1 text-[24px] font-bold tabular-nums">{value}</div>
      <div className="text-[12px] font-semibold">{label}</div>
      <div className="text-[11px] text-muted">best {best}</div>
    </Card>
  );
}

function GoalRow({ goal }: { goal: Goal }) {
  const today = useTodayKey();
  const sessions = alive(useProgress((s) => s.sessions));
  const del = useProgress((s) => s.deleteGoal);
  const p = goalProgress(goal, sessions, today);
  return (
    <div className="row">
      <Ring value={p.today} size={44} stroke={5}><span className="text-[11px] font-bold">{Math.round(p.today * 100)}%</span></Ring>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{goal.title}</div>
        <div className="text-[13px] text-muted">{p.todayLabel}</div>
        {p.overallLabel && (
          <div className="mt-1">
            <div className="h-1.5 overflow-hidden rounded-full bg-fill"><div className="h-full bg-accent" style={{ width: `${(p.overall ?? 0) * 100}%` }} /></div>
            <div className="mt-0.5 text-[12px] text-muted">{p.overallLabel}</div>
          </div>
        )}
      </div>
      <button type="button" aria-label="Delete goal" className="press p-2 text-faint" onClick={() => { if (confirm(`Delete "${goal.title}"?`)) del(goal.id); }}><Trash2 size={16} /></button>
    </div>
  );
}

const PRESETS: Record<GoalKind, { label: string; title: (n: number) => string; def: number; min: number; max: number; unit: string; help: string }> = {
  khatm: { label: 'Khatm', title: (n) => `Complete the Qur'an in ${n} days`, def: 30, min: 3, max: 365, unit: 'days', help: "We'll work out how many pages a day you need, and re-balance if you fall behind." },
  minutes: { label: 'Minutes', title: (n) => `${n} minutes with the Qur'an daily`, def: 15, min: 5, max: 240, unit: 'min/day', help: 'Reading and listening both count.' },
  pages: { label: 'Pages', title: (n) => `Read ${n} pages a day`, def: 4, min: 1, max: 60, unit: 'pages/day', help: 'Counted from the mushaf pages you open and read.' },
  // No longer offered; kept so goals created before still display.
  memorise: { label: 'Minutes', title: (n) => `${n} minutes with the Qur'an daily`, def: 10, min: 5, max: 120, unit: 'min/day', help: 'Reading and listening both count.' },
};
const OFFERED: GoalKind[] = ['khatm', 'minutes', 'pages'];

function GoalWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [kind, setKind] = useState<GoalKind>('khatm');
  const [n, setN] = useState(PRESETS.khatm.def);
  const upsert = useProgress((s) => s.upsertGoal);
  const update = useSettings((s) => s.update);
  const lastRead = useProgress((s) => s.lastRead);
  const [fromCurrent, setFromCurrent] = useState(false);
  const pr = PRESETS[kind];
  const startPage = kind === 'khatm' && fromCurrent && lastRead ? lastRead.page : 1;
  const perDay = kind === 'khatm' ? Math.ceil((605 - startPage) / n) : null;

  const save = () => {
    upsert({ kind, title: pr.title(n), target: n, active: true, startPage: kind === 'khatm' ? startPage : undefined });
    if (kind === 'minutes') update({ dailyGoalMinutes: n });
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title="New goal">
      <div className="px-4 pb-4">
        <Segmented label="Goal type" value={kind} onChange={(k) => { setKind(k); setN(PRESETS[k].def); }}
          options={OFFERED.map((k) => ({ value: k, label: PRESETS[k].label }))} />
      </div>
      <Section footer={pr.help}>
        <Row label={pr.unit}><Stepper value={n} min={pr.min} max={pr.max} onChange={setN} /></Row>
        {kind === 'khatm' && lastRead && (
          <Row label={`Start from page ${lastRead.page}`}><input type="checkbox" checked={fromCurrent} onChange={(e) => setFromCurrent(e.target.checked)} className="h-5 w-5 accent-[var(--accent)]" /></Row>
        )}
        {perDay && <Row label="Daily target" detail={`${perDay} pages (≈ ${Math.round(perDay * 1.6)} min)`} />}
      </Section>
      <div className="px-4">
        <button type="button" onClick={save} className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink">{pr.title(n)}</button>
      </div>
    </Sheet>
  );
}
